import { useStatsigClient } from '@statsig/react-bindings'

import { STATSIG_EXPERIMENTS } from '../../config/statsig-experiments.config'

import { DEFAULT_EXPERIMENT_TARGETS, ExperimentTargetsSchema } from './experiment-targets.types'
import { useStatsigInitContext } from './statsig.context'
import { useStatsigExperiment } from './useStatsigExperiment'

/**
 * Reads a per-experiment targets dynamic config from Statsig.
 * Falls back to defaultValue when Statsig is not yet initialised or the config is missing.
 * Fail-open: any read error returns the default (empty targets list).
 */
function useExperimentTargetsConfig<T>(configKey: string | undefined, defaultValue: ExperimentTargetsSchema<T>): T[] {
  const { getDynamicConfig } = useStatsigClient()
  const { init } = useStatsigInitContext()
  if (!init || !configKey) return defaultValue.targets
  try {
    const value = getDynamicConfig(configKey).value as ExperimentTargetsSchema<T> | null | undefined
    return value?.targets ?? defaultValue.targets
  } catch {
    return defaultValue.targets
  }
}

/**
 * Scoped Statsig experiment hook — exposure only fires when matchTargets returns true.
 *
 * Reads the `targetsConfigKey` dynamic config (shape: `{ targets: T[] }`), runs the
 * caller-supplied matcher, and forwards `isEligible` as `shouldFetchExperiment` to
 * the underlying `useStatsigExperiment`.
 *
 * Non-eligible renders return `experimentBucket: null` with no exposure logged.
 * Before the dynamic config has loaded the targets list is empty, so the default
 * no-exposure behaviour is preserved on first render.
 *
 * @param experimentKey       The Statsig experiment to scope
 * @param targetsConfigKey    Statsig dynamic config key (`<experiment_key>_targets`)
 * @param matchTargets        Pure function — receives the targets array, returns eligibility
 * @param defaultTargets      Fallback schema when the config is unavailable (default: empty list)
 */
export function useTargetedStatsigExperiment<T>(
  experimentKey: STATSIG_EXPERIMENTS,
  targetsConfigKey: string | undefined,
  matchTargets: (targets: T[]) => boolean,
  defaultTargets: ExperimentTargetsSchema<T> = DEFAULT_EXPERIMENT_TARGETS as ExperimentTargetsSchema<T>,
) {
  const targets = useExperimentTargetsConfig<T>(targetsConfigKey, defaultTargets)
  return useStatsigExperiment(experimentKey, matchTargets(targets))
}
